/* eslint-disable @typescript-eslint/no-explicit-any */
// CR085: Evidence Drill-Down Side Panel
// CR086: Enhanced to fetch full OCR text from storage URL
// CR229: Added Drop Reason + OCR Metadata display
// CR161: Added AI OCR source selector and AiCallLink integration
// CR164: Integrated FullOcrViewer and LocatorResultsPanel
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Image, FileText, Zap, CheckCircle, XCircle, ExternalLink, 
  AlertCircle, Clock, BarChart2, Loader2, Ban, Ruler, Layers, Eye, Brain, Target
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TruthTablePage, PageEvidence } from "./types";
import { AiCallLink } from "../AiCallLink";
import { FullOcrViewer } from "./FullOcrViewer";
import { LocatorResultsPanel } from "./LocatorResultsPanel";
import type { PageLocatorResults, LocatorRegion } from "@/lib/flyer-pipeline-v2/dealLocatorTypes";
import { deriveCandidateResultLabel } from "./signalDebug";

interface FlyerEvidencePanelProps {
  page: TruthTablePage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigateToStaging?: (stagingId: string) => void;
}

// CR161: OCR source type for selector
type OcrSource = "tesseract" | "ai";

export function FlyerEvidencePanel({
  page,
  open,
  onOpenChange,
  onNavigateToStaging,
}: FlyerEvidencePanelProps) {
  const [evidence, setEvidence] = useState<PageEvidence | null>(null);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [fullOcrText, setFullOcrText] = useState<string | null>(null);
  const [ocrTextLoading, setOcrTextLoading] = useState(false);
  // CR161: OCR source selector state
  const [ocrSource, setOcrSource] = useState<OcrSource>("tesseract");
  // CR161: Raw AI output viewer state
  const [showRawJson, setShowRawJson] = useState(false);

  // Fetch evidence and signed URL when page changes
  useEffect(() => {
    if (!page || !open) {
      setEvidence(null);
      setImageUrl(null);
      setFullOcrText(null);
      return;
    }

    const fetchEvidence = async () => {
      setLoading(true);
      setImageError(null);
      
      try {
        // Build evidence from page data
        const ev: PageEvidence = {
          ocrText: page.ocrText, // Preview text from page data
          ocrConfidence: page.ocrConfidence,
          ocrDurationMs: page.ocrDurationMs,
          totalScore: page.signalScore,
          threshold: page.signalThreshold,
          signals: page.triggeredSignals?.map((name) => ({
            name,
            score: 0, // We don't have individual scores in current data
            matched: true,
          })),
          // CR161: AI OCR data
          aiOcrText: page.aiOcrText,
          aiOcrModel: page.aiOcrModel,
          aiOcrCallId: page.aiOcrCallId,
          // CR161: Raw AI output
          aiRawOutput: page.aiRawOutput,
          aiCallId: page.aiCallId,
        };
        setEvidence(ev);

        // Get signed URL for image if render path exists
        if (page.renderPath) {
          const { data, error } = await supabase.storage
            .from("flyer-fetch-assets")
            .createSignedUrl(page.renderPath, 600); // 10 min expiry
          
          if (error) {
            console.error("Failed to get signed URL:", error);
            setImageError("Failed to load page image");
          } else if (data?.signedUrl) {
            setImageUrl(data.signedUrl);
          }
        }

      // CR465: Lazy-load full OCR text from cr075_flyer_page_truth
      {
        setOcrTextLoading(true);
        try {
          // First try storage URL if available
          if ((page as any).ocrTextUrl) {
            const { data: signedData, error: signedError } = await supabase.storage
              .from("flyer-fetch-assets")
              .createSignedUrl((page as any).ocrTextUrl, 600);
            
            if (!signedError && signedData?.signedUrl) {
              const textResponse = await fetch(signedData.signedUrl);
              if (textResponse.ok) {
                const text = await textResponse.text();
                setFullOcrText(text);
                setOcrTextLoading(false);
                return; // early exit within fetchEvidence scope is fine
              }
            }
          }

          // CR465: Fetch ocr_full_text directly from truth table
          const { data: truthRow } = await supabase
            .from("cr075_flyer_page_truth")
            .select("ocr_full_text, ocr_text_preview")
            .eq("batch_run_id", (page as any).flyerId?.includes("-") ? page.flyerId : "")
            .eq("flyer_merchant", page.flyerMerchant)
            .eq("page_number", page.pageNumber)
            .maybeSingle();

          // If the flyerId-based query didn't work, try by the page id directly
          if (!truthRow) {
            const { data: truthById } = await supabase
              .from("cr075_flyer_page_truth")
              .select("ocr_full_text, ocr_text_preview")
              .eq("id", page.id)
              .maybeSingle();
            
            if (truthById?.ocr_full_text) {
              setFullOcrText(truthById.ocr_full_text);
            }
          } else if (truthRow?.ocr_full_text) {
            setFullOcrText(truthRow.ocr_full_text);
          }
        } catch (err) {
          console.error("Failed to fetch full OCR text:", err);
        } finally {
          setOcrTextLoading(false);
        }
      }
      } catch (err) {
        console.error("Error fetching evidence:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvidence();
  }, [page, open]);

  if (!page) return null;

  const signalBreakdown = (page.signalBreakdown ?? null) as Record<string, unknown> | null;
  const signalHitsById =
    signalBreakdown && typeof signalBreakdown === "object" && !Array.isArray(signalBreakdown)
      ? (signalBreakdown.hits_by_signal as Record<string, unknown> | undefined)
      : undefined;
  const signalSummaryRows = signalHitsById
    ? Object.entries(signalHitsById)
        .filter(([, count]) => Number(count ?? 0) > 0)
        .map(([signalId, count]) => ({ signalId, count: Number(count ?? 0) }))
    : [];

  const signalScore = Number(page.signalScore ?? 0);
  const signalThreshold = Number(page.signalThreshold ?? 12);
  const candidateResultText = deriveCandidateResultLabel(
    page.isCandidate,
    signalScore,
    signalThreshold,
    signalBreakdown as Record<string, unknown> | null | undefined,
  );

  // Display text: prefer full text, fall back to preview
  const displayOcrText = fullOcrText || evidence?.ocrText;
  
  // CR229: Calculate page megapixels for display
  const pageMegapixels = page.renderWidth && page.renderHeight 
    ? ((page.renderWidth * page.renderHeight) / 1_000_000).toFixed(1)
    : null;

  // CR164: Build locator results from page data (if available)
  const buildLocatorResults = (p: TruthTablePage): PageLocatorResults | null => {
    // Try to get locator data from page evidence
    // For now, return a placeholder structure - data will be populated when pipeline produces it
    const locatorData = (p as any).locatorResults as PageLocatorResults | undefined;
    if (locatorData) {
      return locatorData;
    }
    // Return null if no locator data exists yet
    return null;
  };

  // CR164: Handle "Run AI on crop" action
  const handleRunAiOnCrop = async (region: LocatorRegion) => {
    // TODO: Implement crop-based AI extraction call
    console.log("Run AI on crop:", region);
    // This would call the extraction endpoint with the specific crop coordinates
    // For now, show a toast indicating the feature is coming
  };

  const getOutcomeBadge = () => {
    switch (page.outcome) {
      case "DEAL_CREATED":
        return <Badge className="bg-green-600">Deal Created</Badge>;
      case "CANDIDATE":
        return <Badge variant="secondary">Candidate</Badge>;
      case "OCR_COMPLETE":
        return <Badge variant="outline">OCR Only</Badge>;
      // CR176: Downscale outcomes
      case "OCR_DOWNSCALED_OK":
        return <Badge variant="outline" className="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">OCR (Downscaled)</Badge>;
      case "OCR_DOWNSCALED_EMPTY":
        return <Badge variant="outline" className="bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300">OCR Empty (Downscaled)</Badge>;
      case "OCR_DOWNSCALED_FAILED":
        return <Badge variant="destructive">Downscale Failed</Badge>;
      case "OCR_SKIPPED":
        return <Badge variant="outline" className="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">OCR Skipped</Badge>;
      case "RENDERED":
        return <Badge variant="outline">Rendered Only</Badge>;
      case "OCR_FAILED":
        return <Badge variant="destructive">OCR Failed</Badge>;
      case "RENDER_FAILED":
        return <Badge variant="destructive">Render Failed</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };
  
  // CR229/CR176: Get drop reason display
  const getDropReasonDisplay = () => {
    if (!page.dropReason && !page.ocrSkipped) return null;
    
    const reasons: Record<string, { label: string; description: string; icon: React.ReactNode }> = {
      "BELOW_THRESHOLD": {
        label: "Below Signal Threshold",
        description: `Signal score ${page.signalScore ?? 0} is below the threshold of ${page.signalThreshold ?? 12}. Page was OCR'd but did not match enough deal signals.`,
        icon: <BarChart2 className="h-4 w-4" />,
      },
      "OCR_SKIPPED_SIZE": {
        label: "Page Too Large for OCR",
        description: `Page dimensions (${page.renderWidth}×${page.renderHeight}, ${pageMegapixels}MP) exceed browser OCR limits. Consider using server-side OCR for large pages.`,
        icon: <Ruler className="h-4 w-4" />,
      },
      "OCR_SKIPPED_TIMEOUT": {
        label: "OCR Timed Out",
        description: "OCR processing timed out on this page. The page may be too complex or the worker was overloaded.",
        icon: <Clock className="h-4 w-4" />,
      },
      "OCR_NO_TEXT": {
        label: "No Text Extracted",
        description: page.ocrInputVariant === "ocr_safe" 
          ? `OCR ran on downscaled image (${page.ocrSafeWidth}×${page.ocrSafeHeight}) but extracted no readable text.`
          : "OCR ran but extracted no readable text. The page may be image-only or contain non-text content.",
        icon: <FileText className="h-4 w-4" />,
      },
      // CR176: Add downscale-specific drop reasons
      "DOWNSCALE_FAILED": {
        label: "Downscale Failed",
        description: `Failed to generate OCR-safe image for this oversized page (${pageMegapixels}MP). The page could not be processed.`,
        icon: <Ruler className="h-4 w-4" />,
      },
      "AI_NO_DEAL": {
        label: "AI Found No Deal",
        description: "AI scanned this page but did not identify any gift card deals.",
        icon: <Zap className="h-4 w-4" />,
      },
      "AWAITING_OCR": {
        label: "Awaiting OCR",
        description: "This page has not been processed by OCR yet.",
        icon: <Clock className="h-4 w-4" />,
      },
      "UNKNOWN": {
        label: "Unknown Reason",
        description: page.ocrSkipReason || "Page was not processed for an unknown reason.",
        icon: <AlertCircle className="h-4 w-4" />,
      },
    };
    
    const reason = reasons[page.dropReason || "UNKNOWN"] || reasons["UNKNOWN"];
    return reason;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-hidden flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {page.flyerMerchant} – Page {page.pageNumber}
          </SheetTitle>
          <SheetDescription className="flex items-center gap-2">
            {getOutcomeBadge()}
            <span className="text-muted-foreground">{page.outcomeReason}</span>
          </SheetDescription>
        </SheetHeader>

        <Separator className="my-4" />

        <Tabs defaultValue="render" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="flex-shrink-0 grid w-full grid-cols-5">
            <TabsTrigger value="render" className="text-xs">
              <Image className="h-3 w-3 mr-1" />
              Image
            </TabsTrigger>
            <TabsTrigger value="ocr" className="text-xs">
              <FileText className="h-3 w-3 mr-1" />
              OCR
            </TabsTrigger>
            <TabsTrigger value="signals" className="text-xs">
              <BarChart2 className="h-3 w-3 mr-1" />
              Signals
            </TabsTrigger>
            <TabsTrigger value="ai" className="text-xs" disabled={!page.aiScanned}>
              <Zap className="h-3 w-3 mr-1" />
              AI
            </TabsTrigger>
            {/* CR164: Locator Results tab */}
            <TabsTrigger value="locator" className="text-xs">
              <Target className="h-3 w-3 mr-1" />
              Locator
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 mt-4">
            {/* Render Evidence Tab */}
            <TabsContent value="render" className="mt-0 space-y-4">
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Page Image</h4>
                {loading ? (
                  <Skeleton className="w-full aspect-[8.5/11] rounded-lg" />
                ) : imageError ? (
                  <div className="w-full aspect-[8.5/11] rounded-lg bg-muted flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                      <p className="text-sm">{imageError}</p>
                    </div>
                  </div>
                ) : imageUrl ? (
                  <img 
                    src={imageUrl} 
                    alt={`Page ${page.pageNumber}`}
                    className="w-full rounded-lg border shadow-sm"
                  />
                ) : (
                  <div className="w-full aspect-[8.5/11] rounded-lg bg-muted flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <Image className="h-8 w-8 mx-auto mb-2" />
                      <p className="text-sm">No image available</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="border rounded-lg p-3">
                  <p className="text-muted-foreground">Page Number</p>
                  <p className="font-medium">{page.pageNumber}</p>
                </div>
                <div className="border rounded-lg p-3">
                  <p className="text-muted-foreground">Rendered</p>
                  <p className="font-medium flex items-center gap-1">
                    {page.rendered ? (
                      <><CheckCircle className="h-4 w-4 text-green-500" /> Yes</>
                    ) : (
                      <><XCircle className="h-4 w-4 text-muted-foreground" /> No</>
                    )}
                  </p>
                </div>
                {page.renderWidth && page.renderHeight && (
                  <>
                    <div className="border rounded-lg p-3">
                      <p className="text-muted-foreground">Resolution</p>
                      <p className="font-medium">{page.renderWidth} × {page.renderHeight} px</p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <p className="text-muted-foreground">Size</p>
                      <p className="font-medium">{pageMegapixels} megapixels</p>
                    </div>
                  </>
                )}
              </div>
              
              {/* CR229: Drop Reason Section */}
              {(page.dropReason || page.ocrSkipped) && (() => {
                const dropInfo = getDropReasonDisplay();
                if (!dropInfo) return null;
                return (
                  <div className="mt-4 p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                        {dropInfo.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          <Ban className="h-4 w-4" />
                          Drop Reason: {dropInfo.label}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {dropInfo.description}
                        </p>
                        {page.ocrSkipped && (
                          <div className="mt-2 text-xs bg-background/50 p-2 rounded border">
                            <span className="font-medium">Raw skip reason:</span> {page.ocrSkipReason || "Not specified"}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </TabsContent>

            {/* OCR Evidence Tab */}
            <TabsContent value="ocr" className="mt-0 space-y-4">
              {/* CR173: OCR Input Section */}
              <div className="p-3 rounded-lg bg-muted/30 border">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  OCR Input
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Outcome</p>
                    <Badge 
                      variant={
                        page.ocrOutcome?.includes("OK") ? "default" :
                        page.ocrOutcome?.includes("EMPTY") ? "secondary" :
                        page.ocrOutcome?.includes("SKIPPED") ? "outline" :
                        "destructive"
                      }
                      className="text-xs mt-0.5"
                    >
                      {page.ocrOutcome || (page.ocrComplete ? "OCR_OK" : page.ocrSkipped ? "OCR_SKIPPED" : "PENDING")}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Input Used</p>
                    <p className="font-medium">
                      {page.ocrInputVariant === "ocr_safe" ? (
                        <span className="text-amber-600 dark:text-amber-400">OCR Safe (downscaled)</span>
                      ) : (
                        <span>Full</span>
                      )}
                    </p>
                  </div>
                </div>
                
                {/* Dimensions breakdown */}
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div className="border rounded p-2 bg-background">
                    <p className="text-muted-foreground">Full Render</p>
                    <p className="font-mono">
                      {page.renderWidth && page.renderHeight 
                        ? `${page.renderWidth}×${page.renderHeight}` 
                        : "—"}
                    </p>
                    <p className="text-muted-foreground">
                      {page.renderMegapixels 
                        ? `${page.renderMegapixels.toFixed(1)} MP` 
                        : pageMegapixels 
                          ? `${pageMegapixels} MP` 
                          : "—"}
                    </p>
                  </div>
                  {page.ocrInputVariant === "ocr_safe" && (
                    <div className="border rounded p-2 bg-amber-50 dark:bg-amber-900/20">
                      <p className="text-muted-foreground">OCR Safe</p>
                      <p className="font-mono">
                        {page.ocrSafeWidth && page.ocrSafeHeight 
                          ? `${page.ocrSafeWidth}×${page.ocrSafeHeight}` 
                          : "Pending"}
                      </p>
                      <p className="text-muted-foreground">
                        {page.ocrSafeMegapixels 
                          ? `${page.ocrSafeMegapixels.toFixed(1)} MP` 
                          : "—"}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Engine info */}
                {page.ocrEngineUsed && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Engine: {page.ocrEngineUsed}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="border rounded-lg p-3">
                  <p className="text-muted-foreground">OCR Status</p>
                  <p className="font-medium flex items-center gap-1">
                    {page.ocrSkipped ? (
                      <><AlertCircle className="h-4 w-4 text-amber-500" /> Skipped</>
                    ) : page.ocrComplete ? (
                      <><CheckCircle className="h-4 w-4 text-green-500" /> Complete</>
                    ) : (
                      <><XCircle className="h-4 w-4 text-muted-foreground" /> Not Run</>
                    )}
                  </p>
                </div>
                {page.ocrConfidence !== undefined && !page.ocrSkipped && (
                  <div className="border rounded-lg p-3">
                    <p className="text-muted-foreground">OCR Score</p>
                    <p className="font-medium">{page.ocrConfidence}</p>
                  </div>
                )}
                {evidence?.ocrDurationMs !== undefined && !page.ocrSkipped && (
                  <div className="border rounded-lg p-3">
                    <p className="text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Duration
                    </p>
                    <p className="font-medium">{evidence.ocrDurationMs}ms</p>
                  </div>
                )}
              </div>
              
              {/* CR229: OCR Skip explanation */}
              {page.ocrSkipped && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">OCR was skipped for this page</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">{page.ocrSkipReason || "No reason recorded"}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* CR161: OCR Source Selector */}
              {(page.aiOcrText || evidence?.aiOcrText) && (
                <div className="p-3 rounded-lg border bg-muted/20">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      OCR Source
                    </h4>
                    <Select value={ocrSource} onValueChange={(v) => setOcrSource(v as OcrSource)}>
                      <SelectTrigger className="w-[160px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tesseract">
                          <span className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            Tesseract OCR
                          </span>
                        </SelectItem>
                        <SelectItem value="ai">
                          <span className="flex items-center gap-1">
                            <Brain className="h-3 w-3" />
                            AI OCR ({page.aiOcrModel || evidence?.aiOcrModel || "Gemini"})
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {ocrSource === "ai" && (page.aiOcrCallId || evidence?.aiOcrCallId) && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>AI Call:</span>
                      <AiCallLink
                        aiCallId={(page.aiOcrCallId || evidence?.aiOcrCallId) ?? ""}
                        pageNumber={page.pageNumber}
                        model={page.aiOcrModel || evidence?.aiOcrModel}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* CR164: Replace basic OCR block with FullOcrViewer */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    Extracted Text
                    {ocrTextLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                    {ocrSource === "ai" && (
                      <Badge variant="outline" className="text-xs">AI Source</Badge>
                    )}
                  </h4>
                  {/* CR466: Show captured char count from truth table */}
                  {page.ocrCharCount != null && (
                    <span className="text-xs text-muted-foreground">
                      Captured: <span className="font-mono font-medium">{page.ocrCharCount.toLocaleString()}</span> chars
                    </span>
                  )}
                </div>
                {ocrSource === "tesseract" ? (
                  <FullOcrViewer
                    previewText={evidence?.ocrText}
                    fullText={fullOcrText ?? undefined}
                    isLoading={ocrTextLoading}
                    pageNumber={page.pageNumber}
                    merchantName={page.flyerMerchant}
                  />
                ) : (
                  /* AI OCR text - still using FullOcrViewer */
                  <FullOcrViewer
                    previewText={page.aiOcrText || evidence?.aiOcrText}
                    fullText={page.aiOcrText || evidence?.aiOcrText}
                    isLoading={false}
                    pageNumber={page.pageNumber}
                    merchantName={page.flyerMerchant}
                  />
                )}
              </div>
            </TabsContent>

            {/* Signals Evidence Tab */}
            <TabsContent value="signals" className="mt-0 space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="border rounded-lg p-3">
                  <p className="text-muted-foreground">Signal Score</p>
                  <p className="font-medium text-lg">
                    {page.signalScore?.toFixed(2) ?? "—"}
                  </p>
                </div>
                <div className="border rounded-lg p-3">
                  <p className="text-muted-foreground">Threshold</p>
                  <p className="font-medium text-lg">
                    {page.signalThreshold?.toFixed(2) ?? "—"}
                  </p>
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <p className="text-muted-foreground mb-1">Result</p>
                <p className="font-medium flex items-center gap-2">
                  {page.isCandidate ? (
                    <><CheckCircle className="h-5 w-5 text-green-500" /> {candidateResultText}</>
                  ) : (
                    <><XCircle className="h-5 w-5 text-muted-foreground" /> {candidateResultText}</>
                  )}
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium">Signals Fired</h4>
                {page.triggeredSignals && page.triggeredSignals.length > 0 ? (
                  <div className="space-y-1">
                    {page.triggeredSignals.map((signal, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm border rounded px-2 py-1.5 bg-green-50 dark:bg-green-900/20">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span>{signal}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground border rounded-lg p-3">
                    No signals fired
                  </div>
                )}
              </div>

              {signalSummaryRows.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Signal Hit Summary (Persisted)</h4>
                  <div className="space-y-1">
                    {signalSummaryRows.map((row) => (
                      <div key={row.signalId} className="flex items-center justify-between text-sm border rounded px-2 py-1.5">
                        <span className="font-mono">{row.signalId}</span>
                        <Badge variant="outline">hits: {row.count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {signalBreakdown && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Raw Signal Debug Payload</h4>
                  <ScrollArea className="h-40 border rounded-lg p-3 bg-muted/30">
                    <pre className="text-xs whitespace-pre-wrap font-mono">
                      {JSON.stringify(signalBreakdown, null, 2)}
                    </pre>
                  </ScrollArea>
                </div>
              )}
            </TabsContent>

            {/* AI Evidence Tab */}
            <TabsContent value="ai" className="mt-0 space-y-4">
              {page.aiScanned ? (
                <>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="border rounded-lg p-3">
                      <p className="text-muted-foreground">AI Scanned</p>
                      <p className="font-medium flex items-center gap-1">
                        <CheckCircle className="h-4 w-4 text-green-500" /> Yes
                      </p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <p className="text-muted-foreground">Deal Extracted</p>
                      <p className="font-medium flex items-center gap-1">
                        {page.aiDealExtracted ? (
                          <><CheckCircle className="h-4 w-4 text-green-500" /> Yes</>
                        ) : (
                          <><XCircle className="h-4 w-4 text-muted-foreground" /> No</>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* CR161: AI Call Link + Model Info */}
                  <div className="border rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-muted-foreground">Model Used</p>
                        <p className="font-medium">{page.aiModel || "—"}</p>
                      </div>
                      {page.aiCallId && (
                        <div className="text-right">
                          <p className="text-muted-foreground text-xs">AI Call</p>
                          <AiCallLink
                            aiCallId={page.aiCallId}
                            pageNumber={page.pageNumber}
                            model={page.aiModel}
                            durationMs={page.aiDurationMs}
                            rawOutput={page.aiRawOutput}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* CR158: AI Duration and Timeout Info */}
                  {page.aiDurationMs !== undefined && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="border rounded-lg p-3">
                        <p className="text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" /> Duration
                        </p>
                        <p className="font-medium">{(page.aiDurationMs / 1000).toFixed(1)}s</p>
                      </div>
                      {page.aiTimeoutMs && (
                        <div className="border rounded-lg p-3">
                          <p className="text-muted-foreground">Timeout Setting</p>
                          <p className="font-medium">{(page.aiTimeoutMs / 1000).toFixed(0)}s</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* CR158: Timeout Error Display */}
                  {page.aiTimedOut && (
                    <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-destructive/20 text-destructive">
                          <Clock className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-sm text-destructive">AI Call Timed Out</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Timed out after {page.aiDurationMs ? (page.aiDurationMs / 1000).toFixed(1) : '?'}s
                            {page.aiActivePhase && ` during "${page.aiActivePhase}" phase`}
                          </p>
                          {page.aiTimeoutMs && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Timeout setting: {(page.aiTimeoutMs / 1000).toFixed(0)}s — Consider increasing in S0 Scan Configuration → Advanced
                            </p>
                          )}
                          
                          {/* Phase timings breakdown */}
                          {page.aiPhaseTimings && Object.keys(page.aiPhaseTimings).length > 0 && (
                            <div className="mt-3 text-xs bg-background/50 p-2 rounded border">
                              <p className="font-medium mb-1">Phase Timings:</p>
                              <div className="grid grid-cols-2 gap-1">
                                {Object.entries(page.aiPhaseTimings).map(([phase, ms]) => (
                                  <div key={phase} className="flex justify-between">
                                    <span className="text-muted-foreground">{phase}:</span>
                                    <span className={phase === page.aiActivePhase ? "text-destructive font-medium" : ""}>
                                      {ms !== null ? `${ms}ms` : "—"}
                                      {phase === page.aiActivePhase && " ⚠"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CR160: Multi-brand variant indicator */}
                  {page.isMultiBrandVariant && (
                    <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          <Layers className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-sm text-blue-700 dark:text-blue-300">Multi-Brand Offer</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            This deal was exploded from a multi-brand offer into individual brand variants.
                            {page.multiBrandVariantCount && (
                              <span className="font-medium"> ({page.multiBrandVariantCount} brands)</span>
                            )}
                          </p>
                          {page.variantNotes && (
                            <p className="text-xs text-muted-foreground mt-1 italic">{page.variantNotes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {page.aiDealSummary && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Deal Summary</h4>
                      <div className="border rounded-lg p-3 bg-green-50 dark:bg-green-900/20">
                        <p className="text-sm">{page.aiDealSummary}</p>
                      </div>
                    </div>
                  )}

                  {/* CR161: Raw AI Output JSON Viewer */}
                  {page.aiRawOutput && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium flex items-center gap-2">
                          <Eye className="h-4 w-4" />
                          Raw AI Output
                        </h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowRawJson(!showRawJson)}
                          className="text-xs"
                        >
                          {showRawJson ? "Hide JSON" : "View JSON"}
                        </Button>
                      </div>
                      {showRawJson && (
                        <ScrollArea className="h-64 border rounded-lg p-3 bg-muted/30">
                          <pre className="text-xs whitespace-pre-wrap font-mono">
                            {JSON.stringify(page.aiRawOutput, null, 2)}
                          </pre>
                        </ScrollArea>
                      )}
                    </div>
                  )}

                  {page.stagingDealId && onNavigateToStaging && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => onNavigateToStaging(page.stagingDealId!)}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View in Staging
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>This page was not sent to AI</p>
                  <p className="text-xs mt-1">{page.outcomeReason}</p>
                </div>
              )}
            </TabsContent>

            {/* CR164: Locator Results Tab */}
            <TabsContent value="locator" className="mt-0 space-y-4">
              <LocatorResultsPanel
                results={buildLocatorResults(page)}
                onRunAiOnCrop={handleRunAiOnCrop}
                supportsOverlay={false}
              />
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
